import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Plus,
  FolderOpen,
  FilePlus2,
  ScrollText,
  Wrench
} from 'lucide-react';
import { useTranslation } from 'react-i18next'; // Importar hook de traducción

import {
  updateProjectContext,
  updateProjectInstructions,
  deleteProject
} from '../../AGTHall/services/AGTHallService';
import FilesView from './views/FilesView';
import ChatsView from './views/ChatsView';
import ContextView from './views/ContextView';
import InstructionsView from './views/InstructionsView';
import { ProjectContextProvider, useProjectContext } from './ProjectContextProvider';

const ProjectHeader = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: '54px',
  zIndex: 1100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor: isWallpaperMode
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
  borderBottom: 'none',
  };
});

const CategoryChip = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
  display: 'inline-flex',
  alignItems: 'center',
  height: '20px',
  padding: '0 8px',
  borderRadius: '10px',
  backgroundColor:
    isWallpaperMode
      ? 'rgba(255, 255, 255, 0.22)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(3, 133, 255, 0.18)'
        : 'rgba(3, 133, 255, 0.14)'),
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.2px',
  textTransform: 'capitalize',
  marginLeft: theme.spacing(1),
  };
});

const StyledButton = styled(Button)(() => ({
  backgroundColor: '#0385FF',
  color: '#FFFFFF',
  borderRadius: '20px',
  textTransform: 'none',
  padding: '6px 16px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#0371db',
    boxShadow: 'none',
  },
  '& .MuiButton-startIcon': {
    marginRight: 6,
  },
}));

const ProjectChatViewContent = ({
  project,
  userId,
  selectedModel,
  selectedPersonality,
  isWaitingForResponse,
  inputRef,
  rateLimitInfo,
  onOpenAGTPromo,
  onModelChange,
  onPersonalityChange,
  isPro,
  error,
  onErrorClose,
  lastAssistantMessage,
  handleSendMessage,
  toggleSidebar,
  onProjectChatSelect,
  onProjectDeleted,
}) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const { t } = useTranslation(); // Inicializar hook de traducción
  const [currentView, setCurrentView] = useState('main');
  const uploadsDisabled = true;

  // Estados del snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Estados para los modales
  const [openFilesModal, setOpenFilesModal] = useState(false);
  const [openContextModal, setOpenContextModal] = useState(false);
  const [openInstructionsModal, setOpenInstructionsModal] = useState(false);
  const [openDeleteProjectModal, setOpenDeleteProjectModal] = useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  // Campos de context
  const [contextTitle, setContextTitle] = useState('');
  const [contextContent, setContextContent] = useState('');

  // Campos de instructions
  const [instructionsTitle, setInstructionsTitle] = useState('');
  const [instructionsContent, setInstructionsContent] = useState('');

  // Crear un nuevo chat asociado al proyecto
  // MODIFICADO: No crea el chat inmediatamente, solo cambia la vista
  // El chat se creará cuando el usuario envíe el primer mensaje
  const handleCreateNewChatCard = useCallback(() => {
    if (!project || !userId) {
      return;
    }

    // En lugar de crear el chat inmediatamente, solo cambiamos a la vista de chat
    // y dejamos que el primer mensaje cree el chat (evita chats vacíos)
    if (onProjectChatSelect) {
      onProjectChatSelect(null, false, 'chat'); // null = no hay chatId todavía
    }
  }, [project, userId, onProjectChatSelect]);

  // Guardar el contexto del proyecto
  const handleSaveContext = async () => {
    if (!project || !project.id) {
      console.error('[ProjectChatView] No project info. Cannot update context.');
      return;
    }

    try {
      await updateProjectContext({
        userId,
        projectId: project.id,
        contextTitle,
        contextContent
      });

      setSnackbarMessage(t('projectChatView.contextSaved'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error saving context:', err);
      setSnackbarMessage(t('projectChatView.contextSaveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }

    setOpenContextModal(false);
    setContextTitle('');
    setContextContent('');
  };

  // Guardar las instrucciones del proyecto
  const handleSaveInstructions = async () => {
    if (!project || !project.id) {
      console.error('[ProjectChatView] No project info. Cannot update instructions.');
      return;
    }

    try {
      await updateProjectInstructions({
        userId,
        projectId: project.id,
        instructionsTitle,
        instructionsContent
      });

      setSnackbarMessage(t('projectChatView.instructionsSaved'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error saving instructions:', err);
      setSnackbarMessage(t('projectChatView.instructionsSaveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }

    setOpenInstructionsModal(false);
    setInstructionsTitle('');
    setInstructionsContent('');
  };

  const handleConfirmDeleteProject = async () => {
    if (!project?.id || !userId) {
      setOpenDeleteProjectModal(false);
      return;
    }

    setIsDeletingProject(true);
    try {
      await deleteProject(userId, project.id);
      setSnackbarMessage(t('projectsSection.projectDeleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenDeleteProjectModal(false);
      if (onProjectDeleted) {
        onProjectDeleted();
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      setSnackbarMessage(t('projectsSection.failedToDeleteProject'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeletingProject(false);
    }
  };

  // Header: se oculta si currentView === 'chat'
  const renderHeader = () => {
    if (currentView === 'chat') {
      return null;
    }

    return (
      <ProjectHeader>
        <IconButton
          onClick={toggleSidebar}
          sx={{ color: isWallpaperMode ? '#FFFFFF' : '#0385FF' }}
        >
          <MenuIcon size={20} />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '21.25px',
              letterSpacing: '-0.3px',
              textAlign: 'center',
              color: isWallpaperMode ? '#FFFFFF' : 'inherit',
            }}
          >
            {project?.name || t('chat.header.projectChat', { category: '' })}
          </Typography>
          {project?.category && (
            <CategoryChip>{project.category} {t('chat.header.projectChat', { category: '' }).trim()}</CategoryChip>
          )}
        </Box>

        <IconButton
          disabled
          sx={{ visibility: 'hidden', color: isWallpaperMode ? '#FFFFFF' : '#0385FF' }}
        >
          <MenuIcon size={20} />
        </IconButton>
      </ProjectHeader>
    );
  };

  // Determina cuál vista mostrar
  const renderContent = () => {
    switch (currentView) {
      case 'files':
        return (
          <FilesView
            project={project}
            onBack={() => setCurrentView('main')}
            user={{ uid: userId }}
          />
        );

      case 'chats':
        return (
          <ChatsView
            project={project}
            onBack={() => setCurrentView('main')}
            user={{ uid: userId }}
            onChatSelect={(chat) => {
              if (onProjectChatSelect) {
                // Si chat es null, es un nuevo chat que aún no se ha creado
                onProjectChatSelect(chat ? chat.id : null);
              }
            }}
          />
        );

      case 'context':
        return (
          <ContextView
            project={project}
            onBack={() => setCurrentView('main')}
            user={{ uid: userId }}
          />
        );

      case 'instructions':
        return (
          <InstructionsView
            project={project}
            onBack={() => setCurrentView('main')}
            user={{ uid: userId }}
          />
        );

      case 'chat':
        return null;

      default:
        // Pantalla principal con 4 tiles + "Create New Chat"
        return (
          <>
            <Box
              sx={{
                width: '100%',
                maxWidth: 'sm',
                mx: 'auto',
                mt: 2,
                mb: 3,
                p: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={uploadsDisabled ? undefined : () => setOpenFilesModal(true)}
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: uploadsDisabled ? 'not-allowed' : 'pointer',
                      position: 'relative',
                      opacity: uploadsDisabled ? 0.6 : 1,
                      backgroundColor: isWallpaperMode
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'transparent',
                      backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
                      WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
                      border: isWallpaperMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : (theme) => `2px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                        backgroundColor: isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.14)'
                          : 'transparent',
                        '& .icon': {
                          color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                        },
                      },
                    }}
                  >
                    {uploadsDisabled && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          px: 1,
                          py: 0.25,
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: 'rgba(255, 255, 255, 0.18)',
                          color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                          border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.35)' : 'none',
                          backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
                          WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
                        }}
                      >
                        Coming Soon
                      </Box>
                    )}
                    <FilePlus2
                      className="icon"
                      size={32}
                      style={{
                        marginBottom: 8,
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: isWallpaperMode ? '#FFFFFF' : 'inherit' }}
                    >
                      {t('projectChatView.addFiles')}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={() => setOpenContextModal(true)}
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backgroundColor: isWallpaperMode
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'transparent',
                      backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
                      WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
                      border: isWallpaperMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : (theme) => `2px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                        backgroundColor: isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.14)'
                          : 'transparent',
                        '& .icon': {
                          color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                        },
                      },
                    }}
                  >
                    <ScrollText
                      className="icon"
                      size={32}
                      style={{
                        marginBottom: 8,
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: isWallpaperMode ? '#FFFFFF' : 'inherit' }}
                    >
                      {t('projectChatView.addContext')}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={() => setOpenInstructionsModal(true)}
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backgroundColor: isWallpaperMode
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'transparent',
                      backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
                      WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
                      border: isWallpaperMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : (theme) => `2px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                        backgroundColor: isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.14)'
                          : 'transparent',
                        '& .icon': {
                          color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                        },
                      },
                    }}
                  >
                    <Wrench
                      className="icon"
                      size={32}
                      style={{
                        marginBottom: 8,
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: isWallpaperMode ? '#FFFFFF' : 'inherit' }}
                    >
                      {t('projectChatView.addInstructions')}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={handleCreateNewChatCard}
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backgroundColor: isWallpaperMode
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'transparent',
                      backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
                      WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
                      border: isWallpaperMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : (theme) => `2px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                        backgroundColor: isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.14)'
                          : 'transparent',
                        '& .icon': {
                          color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                        },
                      },
                    }}
                  >
                    <Plus
                      className="icon"
                      size={32}
                      style={{
                        marginBottom: 8,
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: isWallpaperMode ? '#FFFFFF' : 'inherit' }}
                    >
                      {t('projectChatView.createNewChat')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ width: '100%', maxWidth: 'sm', mx: 'auto' }}>
              <Divider
                sx={{
                  my: 2,
                  borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.12)' : 'divider',
                }}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: 'sm',
                mx: 'auto',
                mt: 2,
                mb: 3,
                p: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box
                    onClick={uploadsDisabled ? undefined : () => setCurrentView('files')}
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: uploadsDisabled ? 'not-allowed' : 'pointer',
                      position: 'relative',
                      opacity: uploadsDisabled ? 0.6 : 1,
                      transition: 'all 0.2s',
                      '&:hover .icon': {
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      },
                    }}
                  >
                    {uploadsDisabled && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          px: 1,
                          py: 0.25,
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: 'rgba(255, 255, 255, 0.18)',
                          color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                          border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.35)' : 'none',
                          backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
                          WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
                        }}
                      >
                        Coming Soon
                      </Box>
                    )}
                    <FolderOpen
                      className="icon"
                      size={32}
                      style={{
                        marginBottom: 8,
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: isWallpaperMode ? '#FFFFFF' : 'inherit' }}
                    >
                      {t('projectChatView.files')}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box
                    onClick={() => setCurrentView('chats')}
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover .icon': {
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      },
                    }}
                  >
                    <FolderOpen
                      className="icon"
                      size={32}
                      style={{
                        marginBottom: 8,
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: isWallpaperMode ? '#FFFFFF' : 'inherit' }}
                    >
                      {t('projectChatView.chats')}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box
                    onClick={() => setCurrentView('context')}
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover .icon': {
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      },
                    }}
                  >
                    <FolderOpen
                      className="icon"
                      size={32}
                      style={{
                        marginBottom: 8,
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: isWallpaperMode ? '#FFFFFF' : 'inherit' }}
                    >
                      {t('projectChatView.context')}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box
                    onClick={() => setCurrentView('instructions')}
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover .icon': {
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      },
                    }}
                  >
                    <FolderOpen
                      className="icon"
                      size={32}
                      style={{
                        marginBottom: 8,
                        color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: isWallpaperMode ? '#FFFFFF' : 'inherit' }}
                    >
                      {t('projectChatView.instructions')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ width: '100%', maxWidth: 'sm', mx: 'auto', px: 2, pb: 2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => setOpenDeleteProjectModal(true)}
                sx={{
                  width: 'fit-content',
                  alignSelf: 'center',
                  mx: 'auto',
                  display: 'flex',
                  borderRadius: '16px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '12px',
                  px: 1.5,
                  py: 0.5,
                  color: '#FFFFFF',
                  backgroundColor: '#C81E1E',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#B71C1C',
                    boxShadow: 'none',
                  },
                }}
              >
                {t('projectsSection.deleteProject')}
              </Button>
            </Box>
          </>
        );
    }
  };

  return (
    <>
      {renderHeader()}

      <Box
        sx={{
          marginTop: currentView === 'chat' ? 0 : '56px',
          width: '100%',
        }}
      >
        {renderContent()}
      </Box>

      {/* Modal de Files */}
      <FilesView
        project={project}
        user={{ uid: userId }}
        isModal={true}
        open={openFilesModal}
        onClose={() => setOpenFilesModal(false)}
      />

      {/* Modal de Context */}
      <Dialog
        open={openContextModal}
        onClose={() => setOpenContextModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: isWallpaperMode
              ? 'rgba(128, 128, 128, 0.3)'
              : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
            backgroundImage: 'none',
            border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
            backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            pt: 3,
            pb: 2,
            px: 3,
            fontSize: '1.1rem',
            fontWeight: 500,
            borderBottom: 1,
            borderColor: isWallpaperMode
              ? 'rgba(255, 255, 255, 0.2)'
              : (theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'),
            color: isWallpaperMode ? '#FFFFFF' : 'inherit'
          }}
        >
          {t('projectChatView.newContext')}
        </DialogTitle>

        <DialogContent sx={{ mt: 2, p: 3 }}>
          <TextField
            autoFocus
            fullWidth
            placeholder={t('projectChatView.contextTitle')}
            value={contextTitle}
            onChange={(e) => setContextTitle(e.target.value)}
            variant="outlined"
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)'),
                border: 'none',
                '& fieldset': {
                  border: 'none',
                },
              }
            }}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={t('projectChatView.contextPlaceholder')}
            value={contextContent}
            onChange={(e) => setContextContent(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)'),
                border: 'none',
                '& fieldset': {
                  border: 'none',
                },
              }
            }}
          />

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 2,
              color: isWallpaperMode
                ? 'rgba(255, 255, 255, 0.7)'
                : (theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : 'rgba(0, 0, 0, 0.5)'),
              fontSize: '0.7rem',
              textAlign: 'center'
            }}
          >
            {t('projectChatView.contextNote')}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', gap: 1 }}>
          <Button
            onClick={() => setOpenContextModal(false)}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: 3,
              color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
              '&:hover': {
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.05)')
              }
            }}
          >
            {t('projectChatView.cancel')}
          </Button>
          <StyledButton
            onClick={handleSaveContext}
            disabled={!contextContent.trim()}
          >
            {t('projectChatView.save')}
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Modal de Instructions */}
      <Dialog
        open={openInstructionsModal}
        onClose={() => setOpenInstructionsModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: isWallpaperMode
              ? 'rgba(128, 128, 128, 0.3)'
              : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
            backgroundImage: 'none',
            border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
            backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            pt: 3,
            pb: 2,
            px: 3,
            fontSize: '1.1rem',
            fontWeight: 500,
            borderBottom: 1,
            borderColor: isWallpaperMode
              ? 'rgba(255, 255, 255, 0.2)'
              : (theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'),
            color: isWallpaperMode ? '#FFFFFF' : 'inherit'
          }}
        >
          {t('projectChatView.newInstructions')}
        </DialogTitle>

        <DialogContent sx={{ mt: 2, p: 3 }}>
          <TextField
            autoFocus
            fullWidth
            placeholder={t('projectChatView.contextTitle')}
            value={instructionsTitle}
            onChange={(e) => setInstructionsTitle(e.target.value)}
            variant="outlined"
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)'),
                border: 'none',
                '& fieldset': {
                  border: 'none',
                },
              }
            }}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={t('projectChatView.instructionsPlaceholder')}
            value={instructionsContent}
            onChange={(e) => setInstructionsContent(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)'),
                border: 'none',
                '& fieldset': {
                  border: 'none',
                },
              }
            }}
          />

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 2,
              color: isWallpaperMode
                ? 'rgba(255, 255, 255, 0.7)'
                : (theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : 'rgba(0, 0, 0, 0.5)'),
              fontSize: '0.7rem',
              textAlign: 'center'
            }}
          >
            {t('projectChatView.instructionsNote')}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', gap: 1 }}>
          <Button
            onClick={() => setOpenInstructionsModal(false)}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: 3,
              color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
              '&:hover': {
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.05)')
              }
            }}
          >
            {t('projectChatView.cancel')}
          </Button>
          <StyledButton
            onClick={handleSaveInstructions}
            disabled={!instructionsContent.trim()}
          >
            {t('projectChatView.save')}
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Delete Project modal */}
      <Dialog
        open={openDeleteProjectModal}
        onClose={() => setOpenDeleteProjectModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: isWallpaperMode
              ? 'rgba(128, 128, 128, 0.3)'
              : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
            backgroundImage: 'none',
            border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
            backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            pt: 3,
            pb: 2,
            px: 3,
            fontSize: '1.1rem',
            fontWeight: 500,
            borderBottom: 1,
            borderColor: isWallpaperMode
              ? 'rgba(255, 255, 255, 0.2)'
              : (theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'),
            color: isWallpaperMode ? '#FFFFFF' : 'inherit'
          }}
        >
          {t('projectsSection.deleteProject')}
        </DialogTitle>

        <DialogContent sx={{ mt: 2, p: 3 }}>
          <Typography>
            {project?.name ? (
              <>
                {t('projectsSection.deleteConfirmation')} <strong>{project.name}</strong>?{' '}
                {t('projectsSection.deleteWarning')}
              </>
            ) : (
              t('projectsSection.deleteGeneric')
            )}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', gap: 1 }}>
          <Button
            onClick={() => setOpenDeleteProjectModal(false)}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
            }}
            disabled={isDeletingProject}
          >
            {t('projectsSection.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDeleteProject}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              backgroundColor: '#C81E1E',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#B71C1C',
              },
            }}
            disabled={isDeletingProject}
          >
            {isDeletingProject ? <CircularProgress size={20} color="inherit" /> : t('projectsSection.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

// Envolver el componente con ProjectContextProvider
const ProjectChatView = (props) => (
  <ProjectContextProvider>
    <ProjectChatViewContent {...props} />
  </ProjectContextProvider>
);

export default ProjectChatView;
